import PropTypes from "prop-types";
import { useState } from "react";
import { useLocation } from "react-router-dom";

import { PAGE_ROUTES } from "../../constants";
import {
  convertCoverPageDate,
  getFormattedDate,
  getUrlSlug,
  toDollar,
} from "../../helpers";
import useIntakeFormStore from "../../store/IntakeForm.store";
import NavigationButton from "../NavigationButton";
import Popover from "../Popover/index";
import Proposal from "../Proposal";

import "./navigation.scss";

const Navigation = ({
  onNavigateAway,
  isAllSpringTeam,
  pageType = "generic",
  pageData = {},
}) => {
  const [proposalAnchorElement, setProposalAnchorElement] = useState(null);
  const { pathname } = useLocation();
  const paths = getUrlSlug(pathname);
  const isProposalPage =
    pathname !== "/proposals" ||
    (paths.includes("proposals") && paths.length > 1);
  const isAdminPage = paths.includes("admin");
  const isWeeklyReportPage = paths.includes("weekly-reports");
  const isClientQuestionnairePage = paths.includes("client-questionnaire");
  const isProdEnv = process.env.REACT_APP_ENV === `production`;

  const {
    actions: { handleSetIntakeFormValue },
  } = useIntakeFormStore();

  const openProposalPopover = Boolean(proposalAnchorElement);
  const idProposalPopover = openProposalPopover
    ? "proposal-popover"
    : undefined;
  const handleClientClick = (event) => {
    setProposalAnchorElement(event.currentTarget);
  };
  const handleClientClose = () => {
    setProposalAnchorElement(null);
  };

  const proposalsButtonProps = {
    label: "Proposals",
    variant: "link",
    squared: true,
    onClick: () => onNavigateAway(PAGE_ROUTES.proposalListing.getFullPath()),
  };
  const questionnairesButtonProps = {
    label: "Client Questionnaires",
    variant: "link",
    squared: true,
    onClick: () =>
      onNavigateAway(PAGE_ROUTES.clientQuestionnaireListing.getFullPath()),
  };
  const weeklyReportsButtonProps = {
    label: "Weekly Reports",
    variant: "link",
    squared: true,
    onClick: () => onNavigateAway(PAGE_ROUTES.weeklyReports.getFullPath()),
  };

  const generic = () => (
    <div className="navigation__generic">
      <NavigationButton {...proposalsButtonProps} />
      {/* {isAllspringTeam && ( */}
      <NavigationButton {...weeklyReportsButtonProps} />
      {/* )}  */}
      {!isProdEnv && <NavigationButton {...questionnairesButtonProps} />}
    </div>
  );

  const renderNavigationByPageType = {
    generic,
    admin: generic,
    "proposal-listing": () => (
      <div className="navigation__proposal-listing">
        <NavigationButton {...proposalsButtonProps} currentPage />
        <NavigationButton {...weeklyReportsButtonProps} />
        {!isProdEnv && <NavigationButton {...questionnairesButtonProps} />}
      </div>
    ),
    proposal: () => (
      <div className="navigation__proposal">
        <NavigationButton
          label="Back"
          altLabel="Back"
          variant="secondary"
          collapsible
          iconName="system-arrow-left"
          iconSize="small"
          onClick={() => onNavigateAway(-1)}
          className="navigation__back-button"
        />
        {!isAdminPage && !isWeeklyReportPage && !isClientQuestionnairePage && (
          <>
            <Proposal />
            <NavigationButton
              label="Add a new proposal"
              altLabel="Add Proposal"
              variant="outline"
              collapsible
              iconName="system-add"
              iconSize="small"
              onClick={() => {
                const onRedirectConfirmed = () => {
                  handleSetIntakeFormValue(null);
                };
                onNavigateAway(
                  PAGE_ROUTES.proposalCreation.getFullPath(),
                  true,
                  onRedirectConfirmed
                );
              }}
            />
          </>
        )}
        {!isAdminPage && isWeeklyReportPage && isClientQuestionnairePage && (
          <></>
        )}
      </div>
    ),
    "client-questionnaire-listing": () => (
      <div className="navigation__client-questionnaire-listing">
        <NavigationButton {...proposalsButtonProps} />
        <NavigationButton {...weeklyReportsButtonProps} />
        <NavigationButton {...questionnairesButtonProps} currentPage />
      </div>
    ),
    "client-questionnaire": () => {
      const { proposal } = pageData;

      const proposalData = (
        <div className="navigation__client-questionnaire__proposal-data">
          <span className="title">{proposal?.account}</span>

          <div className="row">
            <span>Report Identifier</span>
            <span>{proposal?.reportId || "—"}</span>
          </div>
          <div className="row">
            <span>Date Created</span>
            <span>
              {proposal?.dateCreated
                ? convertCoverPageDate(getFormattedDate(proposal.dateCreated), {
                    isMonthShort: true,
                  })
                : "—"}
            </span>
          </div>
          <div className="row">
            <span>Initial Funding Amount</span>
            <span>
              {proposal?.cashAmount ? toDollar(proposal.cashAmount) : "—"}
            </span>
          </div>
        </div>
      );

      return (
        <div className="navigation__client-questionnaire">
          <NavigationButton
            label="Back"
            altLabel="Back"
            variant="secondary"
            collapsible
            iconName="system-arrow-left"
            iconSize="small"
            onClick={() => onNavigateAway(-1)}
            className="navigation__back-button"
          />

          {proposal && (
            <NavigationButton
              altLabel="Account Information"
              ariaLabel="Account Information"
              collapsible
              iconName="system-info"
              iconSize="small"
              className="navigation__client-questionnaire__account-information-button proposal__summary-alt"
              onClick={handleClientClick}
            />
          )}

          {proposal && proposalData}

          {proposal?.transitionId && (
            <NavigationButton
              label="View Report"
              altLabel="View Report"
              variant="outline"
              collapsible
              alignItems="stretch"
              iconName="system-arrow-up-right"
              iconSize="small"
              className="navigation__client-questionnaire__view-report-button"
              onClick={() =>
                onNavigateAway(
                  PAGE_ROUTES.report.getFullPath(proposal.transitionId),
                  true
                )
              }
            />
          )}

          <Popover
            id={idProposalPopover}
            open={openProposalPopover}
            anchorElement={proposalAnchorElement}
            anchorVertical="top"
            anchorHorizontal="right"
            onClose={handleClientClose}
          >
            {proposalData}
          </Popover>
        </div>
      );
    },
  };

  return (
    <div className="navigation">{renderNavigationByPageType[pageType]()}</div>
  );
};

export const pageTypes = [
  "generic",
  "admin",
  "proposal-listing",
  "proposal",
  "client-questionnaire-listing",
  "client-questionnaire",
];

Navigation.propTypes = {
  onNavigateAway: PropTypes.func,
  pageType: PropTypes.oneOf(pageTypes),
};

Navigation.defaultProps = {
  onNavigateAway: () => {},
  pageType: "generic",
};

export default Navigation;
